import { useState, useEffect } from 'react';

/**
 * Detekuje, zda je zařízení mobilní
 * Používá šířku okna < 1024px jako kritérium
 * @returns true pokud je zařízení mobilní
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;

  // Detekce podle šířky okna (stejná logika jako v App.tsx)
  const isMobileWidth = window.innerWidth < 1024;

  // Detekce touch zařízení pro lepší přesnost
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Kombinace obou kritérií - pokud je úzké okno A má touch, je to mobil
  return isMobileWidth && hasTouch;
}

/**
 * Detekuje, zda aplikace běží v In-App Browseru (Facebook, Instagram)
 * @returns true pokud je detekován IAB
 */
export function isInAppBrowser(): boolean {
  if (typeof window === 'undefined') return false;

  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  // Detekce Facebook (FBAN, FBAV) a Instagram
  return (ua.indexOf("FBAN") > -1) || (ua.indexOf("FBAV") > -1) || (ua.indexOf("Instagram") > -1);
}

/**
 * React hook pro detekci mobilního zařízení
 * Reaktivně reaguje na změny velikosti okna
 * @returns true pokud je zařízení mobilní
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isMobileDevice();
  });

  useEffect(() => {
    const checkMobile = () => {
      try {
        if (typeof window !== 'undefined' && typeof window.innerWidth === 'number') {
          setIsMobile(isMobileDevice());
        }
      } catch (error) {
        console.error('Error checking mobile:', error);
        setIsMobile(false);
      }
    };

    // Zkontrolovat při mount
    checkMobile();

    // Sledovat změny velikosti okna
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return isMobile;
}

/**
 * React hook pro detekci IAB
 * @returns true pokud je aplikace v IAB
 */
export function useIsIAB(): boolean {
  const [isIAB, setIsIAB] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isInAppBrowser();
  });

  useEffect(() => {
    setIsIAB(isInAppBrowser());
  }, []);

  return isIAB;
}
